import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Eye, 
  EyeOff, 
  Type, 
  Contrast, 
  Volume2, 
  VolumeX,
  Settings,
  RotateCcw,
  Keyboard,
  MousePointer
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface AccessibilitySettings {
  highContrast: boolean;
  reducedMotion: boolean;
  fontSize: number;
  focusIndicator: boolean;
  screenReaderMode: boolean;
  keyboardNavigation: boolean;
  colorTheme: 'default' | 'deuteranopia' | 'protanopia' | 'tritanopia';
  announcements: boolean;
}

interface AccessibilityEnhancementsProps {
  className?: string;
}

export function AccessibilityEnhancements({ className }: AccessibilityEnhancementsProps) {
  const { toast } = useToast();
  const [settings, setSettings] = useState<AccessibilitySettings>({
    highContrast: false,
    reducedMotion: false,
    fontSize: 16,
    focusIndicator: true,
    screenReaderMode: false,
    keyboardNavigation: true,
    colorTheme: 'default',
    announcements: true
  });

  const [isOpen, setIsOpen] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        setSettings(prev => ({ ...prev, ...parsedSettings }));
      } catch (error) {
        console.error('Failed to parse accessibility settings:', error);
      }
    }
  }, []);

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // High contrast
    root.classList.toggle('high-contrast', settings.highContrast);
    
    // Reduced motion
    root.classList.toggle('reduce-motion', settings.reducedMotion);
    
    // Font size
    root.style.setProperty('--base-font-size', `${settings.fontSize}px`);
    
    // Focus indicator
    root.classList.toggle('enhanced-focus', settings.focusIndicator);
    
    // Screen reader mode
    root.classList.toggle('screen-reader-mode', settings.screenReaderMode);
    
    // Color theme for color blindness
    root.setAttribute('data-color-theme', settings.colorTheme);

    // Save to localStorage
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K, 
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    if (settings.announcements) {
      announceChange(key, value);
    }
  };

  const announceChange = (key: keyof AccessibilitySettings, value: any) => {
    const messages = {
      highContrast: value ? 'High contrast enabled' : 'High contrast disabled',
      reducedMotion: value ? 'Reduced motion enabled' : 'Reduced motion disabled',
      fontSize: `Font size set to ${value}px`,
      focusIndicator: value ? 'Enhanced focus indicators enabled' : 'Enhanced focus indicators disabled',
      screenReaderMode: value ? 'Screen reader mode enabled' : 'Screen reader mode disabled',
      keyboardNavigation: value ? 'Keyboard navigation enabled' : 'Keyboard navigation disabled',
      colorTheme: `Color theme changed to ${value}`,
      announcements: value ? 'Announcements enabled' : 'Announcements disabled'
    };

    const message = messages[key];
    if (message) {
      // Announce to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = message;
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);

      // Also show toast for visual users
      toast({
        title: "Accessibility Setting Updated",
        description: message,
      });
    }
  };

  const resetSettings = () => {
    const defaultSettings: AccessibilitySettings = {
      highContrast: false,
      reducedMotion: false,
      fontSize: 16,
      focusIndicator: true,
      screenReaderMode: false,
      keyboardNavigation: true,
      colorTheme: 'default',
      announcements: true
    };
    
    setSettings(defaultSettings);
    
    toast({
      title: "Settings Reset",
      description: "All accessibility settings have been reset to defaults",
    });
  };

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className={cn("fixed bottom-4 right-4 z-50", className)}
        aria-label="Open accessibility settings"
      >
        <Settings className="h-4 w-4" />
        <span className="sr-only">Accessibility Settings</span>
      </Button>
    );
  }

  return (
    <Card className={cn("fixed bottom-4 right-4 z-50 w-80 max-h-[70vh] overflow-y-auto", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Accessibility Settings
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(false)}
            aria-label="Close accessibility settings"
          >
            <EyeOff className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Visual Settings */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Visual
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label htmlFor="high-contrast" className="text-sm">High Contrast</label>
              <Switch
                id="high-contrast"
                checked={settings.highContrast}
                onCheckedChange={(checked) => updateSetting('highContrast', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <label htmlFor="reduced-motion" className="text-sm">Reduced Motion</label>
              <Switch
                id="reduced-motion"
                checked={settings.reducedMotion}
                onCheckedChange={(checked) => updateSetting('reducedMotion', checked)}
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="font-size" className="text-sm flex items-center gap-2">
                <Type className="h-3 w-3" />
                Font Size: {settings.fontSize}px
              </label>
              <Slider
                id="font-size"
                min={12}
                max={24}
                step={1}
                value={[settings.fontSize]}
                onValueChange={([value]) => updateSetting('fontSize', value)}
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="color-theme" className="text-sm flex items-center gap-2">
                <Contrast className="h-3 w-3" />
                Color Theme
              </label>
              <Select
                value={settings.colorTheme}
                onValueChange={(value: any) => updateSetting('colorTheme', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="deuteranopia">Deuteranopia</SelectItem>
                  <SelectItem value="protanopia">Protanopia</SelectItem>
                  <SelectItem value="tritanopia">Tritanopia</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Navigation Settings */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Keyboard className="h-4 w-4" />
            Navigation
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label htmlFor="focus-indicator" className="text-sm">Enhanced Focus</label>
              <Switch
                id="focus-indicator"
                checked={settings.focusIndicator}
                onCheckedChange={(checked) => updateSetting('focusIndicator', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <label htmlFor="keyboard-nav" className="text-sm">Keyboard Navigation</label>
              <Switch
                id="keyboard-nav"
                checked={settings.keyboardNavigation}
                onCheckedChange={(checked) => updateSetting('keyboardNavigation', checked)}
              />
            </div>
          </div>
        </div>

        {/* Screen Reader Settings */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Volume2 className="h-4 w-4" />
            Screen Reader
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label htmlFor="screen-reader" className="text-sm">Screen Reader Mode</label>
              <Switch
                id="screen-reader"
                checked={settings.screenReaderMode}
                onCheckedChange={(checked) => updateSetting('screenReaderMode', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <label htmlFor="announcements" className="text-sm">Announcements</label>
              <Switch
                id="announcements"
                checked={settings.announcements}
                onCheckedChange={(checked) => updateSetting('announcements', checked)}
              />
            </div>
          </div>
        </div>

        {/* Reset Button */}
        <div className="pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={resetSettings}
            className="w-full"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// CSS classes that need to be added to globals.css
export const accessibilityStyles = `
/* High contrast mode */
.high-contrast {
  --background: 0 0% 0%;
  --foreground: 0 0% 100%;
  --muted: 0 0% 20%;
  --muted-foreground: 0 0% 80%;
  --border: 0 0% 40%;
  --primary: 210 100% 70%;
  --primary-foreground: 0 0% 0%;
}

/* Enhanced focus indicators */
.enhanced-focus *:focus {
  outline: 3px solid hsl(var(--primary)) !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 5px hsla(var(--primary), 0.3) !important;
}

/* Reduced motion */
.reduce-motion * {
  animation-duration: 0.01ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.01ms !important;
  scroll-behavior: auto !important;
}

/* Font size scaling */
:root {
  --base-font-size: 16px;
}

* {
  font-size: calc(var(--base-font-size) * var(--font-scale, 1)) !important;
}

/* Screen reader mode */
.screen-reader-mode {
  --animation-duration: 0ms;
}

.screen-reader-mode .fade-in,
.screen-reader-mode .slide-in,
.screen-reader-mode .scale-in {
  animation: none !important;
}

/* Color themes for color blindness */
[data-color-theme="deuteranopia"] {
  --primary: 210 100% 50%;
  --secondary: 45 100% 50%;
  --destructive: 0 100% 60%;
  --success: 120 100% 30%;
}

[data-color-theme="protanopia"] {
  --primary: 240 100% 50%;
  --secondary: 60 100% 50%;
  --destructive: 0 100% 60%;
  --success: 180 100% 30%;
}

[data-color-theme="tritanopia"] {
  --primary: 300 100% 50%;
  --secondary: 30 100% 50%;
  --destructive: 0 100% 60%;
  --success: 90 100% 30%;
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
`;